import styled from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import { colors } from '../utils/types';

export const AppContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${colors.dark} 0%, ${colors.darker} 50%, ${colors.darkest} 100%);
  position: relative;
  overflow-x: hidden;
  background: url('/images/assets/bg3.jpg') center center / cover no-repeat fixed;
`;

export const Particles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;

  /* Particle 1 */
  &::before {
    content: '';
    position: absolute;
    width: 4px;
    height: 4px;
    background: ${colors.primary};
    border-radius: 50%;
    top: 20%;
    left: 20%;
    animation: float1 15s linear infinite;
    opacity: 0.6;
  }

  /* Particle 2 */
  &::after {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    background: ${colors.blue};
    border-radius: 50%;
    top: 60%;
    right: 30%;
    animation: float2 20s linear infinite;
    animation-delay: 5s;
    opacity: 0.4;
  }

  /* Additional particles using box-shadow for performance */
  & {
    background-image:
      radial-gradient(2px 2px at 20% 30%, ${colors.purple} 1px, transparent 1px),
      radial-gradient(3px 3px at 70% 60%, ${colors.accent} 1px, transparent 1px);
    background-size: 200px 200px, 250px 250px;
    animation: particleMove 50s linear infinite;
  }

  @keyframes float1 {
    0% {
      transform: translateY(0px) translateX(0px) scale(1);
      opacity: 0;
    }
    10% {
      opacity: 0.6;
    }
    50% {
      transform: translateY(-50vh) translateX(50px) scale(1.2);
    }
    90% {
      opacity: 0.6;
    }
    100% {
      transform: translateY(-100vh) translateX(100px) scale(0.8);
      opacity: 0;
    }
  }

  @keyframes float2 {
    0% {
      transform: translateY(0px) translateX(0px) scale(1);
      opacity: 0;
    }
    10% {
      opacity: 0.4;
    }
    50% {
      transform: translateY(-60vh) translateX(-80px) scale(1.5);
    }
    90% {
      opacity: 0.4;
    }
    100% {
      transform: translateY(-100vh) translateX(-150px) scale(0.5);
      opacity: 0;
    }
  }

  @keyframes particleMove {
    0% {
      background-position: 0 0, 0 0, 0 0, 0 0, 0 0, 0 0, 0 0, 0 0;
    }
    100% {
      background-position: 100px 100px, 150px 150px, 120px 120px, 180px 180px, 90px 90px, 140px 140px, 110px 110px, 160px 160px;
    }
  }
`;

export const FloatingOrbs = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  pointer-events: none;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 80px;
    height: 80px;
    background: radial-gradient(circle, rgba(255, 190, 11, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    top: 15%;
    right: 10%;
    animation: floatOrb1 8s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    width: 60px;
    height: 60px;
    background: radial-gradient(circle, rgba(58, 134, 255, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    bottom: 25%;
    left: 15%;
    animation: floatOrb2 10s ease-in-out infinite reverse;
  }

  @keyframes floatOrb1 {
    0%, 100% {
      transform: translateY(0px) scale(1);
      opacity: 0.3;
    }
    50% {
      transform: translateY(-20px) scale(1.1);
      opacity: 0.6;
    }
  }

  @keyframes floatOrb2 {
    0%, 100% {
      transform: translateY(0px) scale(1);
      opacity: 0.2;
    }
    50% {
      transform: translateY(15px) scale(1.2);
      opacity: 0.5;
    }
  }
`;

export const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  position: relative;
  z-index: 10;
  animation: fadeInDown 0.6s ease-out;

  @media (max-width: 768px) {
    padding: 0.75rem;
  }

  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const Logo = styled.h1`
  font-size: 2.2rem;
  font-weight: 800;
  color: ${colors.text};
  background: linear-gradient(45deg, ${colors.primary}, ${colors.secondary}, ${colors.accent});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  letter-spacing: -0.02em;
  text-shadow: 0 0 30px rgba(255, 190, 11, 0.3);
  animation: logoGlow 3s ease-in-out infinite alternate;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.05rem;
  }

  @keyframes logoGlow {
    from {
      text-shadow: 0 0 30px rgba(255, 190, 11, 0.3);
    }
    to {
      text-shadow: 0 0 40px rgba(255, 190, 11, 0.6), 0 0 60px rgba(255, 190, 11, 0.2);
    }
  }
`;

export const MenuButton = styled.button`
  background: none;
  border: none;
  color: ${colors.text};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

export const MainContent = styled.main`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: calc(100vh - 120px);
  padding: 2rem;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    padding: 1rem 0.75rem;
    min-height: calc(100vh - 60px);
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

export const MixerCard = styled.div<{ $disableAnimation?: boolean }>`
  background: rgba(26, 26, 46, 0.85);
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 2.5rem;
  width: 100%;
  max-width: 850px;
  box-shadow:
    0 25px 50px rgba(0, 0, 0, 0.5),
    0 0 100px rgba(58, 134, 255, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  animation: ${props => props.$disableAnimation ? 'none' : 'fadeInUp 0.8s ease-out'};
  transform: translateY(0);
  position: relative;

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    border-radius: 12px;
    margin: 0 0.75rem;
    max-width: calc(100% - 1.5rem);
  }

  @media (max-width: 480px) {
    padding: 1rem 0.75rem;
    border-radius: 9px;
    margin: 0 0.75rem;
    max-width: calc(100% - 1.5rem);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 24px;
    padding: 1px;
    background: linear-gradient(135deg,
      rgba(255, 190, 11, 0.3),
      rgba(255, 0, 110, 0.3),
      rgba(131, 56, 236, 0.3),
      rgba(58, 134, 255, 0.3)
    );
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: xor;
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    pointer-events: none;

    @media (max-width: 768px) {
      border-radius: 16px;
    }

    @media (max-width: 480px) {
      border-radius: 12px;
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(40px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

export const TabContainer = styled.div`
  display: flex;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 20px;
  padding: 6px;
  margin-bottom: 2.5rem;
  gap: 6px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  position: relative;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    gap: 2px;
    padding: 3px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 20px;
    padding: 1px;
    background: linear-gradient(135deg,
      rgba(255, 190, 11, 0.2),
      rgba(255, 0, 110, 0.2),
      rgba(131, 56, 236, 0.2)
    );
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: xor;
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    pointer-events: none;
  }
`;

export const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 1rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.$active
    ? `linear-gradient(45deg, ${colors.blue}, ${colors.purple})`
    : 'transparent'
  };
  color: ${props => props.$active ? colors.text : colors.textSecondary};

  @media (max-width: 768px) {
    padding: 0.5rem;
    font-size: 0.7rem;
  }

  @media (max-width: 480px) {
    padding: 0.4rem;
    font-size: 0.65rem;
  }

  &:hover {
    background: ${props => props.$active
    ? `linear-gradient(45deg, ${colors.blue}, ${colors.purple})`
    : 'rgba(255, 255, 255, 0.1)'
  };
  }
`;

export const FormContainer = styled.div<{ $disableAnimation?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  animation: ${props => props.$disableAnimation ? 'none' : 'slideIn 0.6s ease-out'};
  transform: translateX(0);

  @media (max-width: 768px) {
    gap: 1rem;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  > * {
    animation: ${props => props.$disableAnimation ? 'none' : 'staggerFadeIn 0.8s ease-out both'};

    &:nth-child(1) { animation-delay: 0.1s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.3s; }
    &:nth-child(4) { animation-delay: 0.4s; }
    &:nth-child(5) { animation-delay: 0.5s; }
    &:nth-child(6) { animation-delay: 0.6s; }
  }

  @keyframes staggerFadeIn {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: 768px) {
    gap: 0.375rem;
  }

  @media (max-width: 480px) {
    gap: 0.25rem;
  }
`;

export const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${colors.textSecondary};

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

export const NetworkSelector = styled.div<{ $isOpen?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: ${props => props.$isOpen ? 10000 : 1};
`;

export const NetworkOption = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: ${props => props.$selected
    ? colors.blue + '20'
    : 'transparent'
  };
  border: 1px solid ${props => props.$selected
    ? colors.blue
    : 'transparent'
  };
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  transform: translateY(0);

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.5rem;
    border-radius: 9px;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    gap: 0.375rem;
    border-radius: 6px;
  }

  &:hover {
    border-color: ${colors.blue};
    background: ${props => props.$selected
    ? colors.blue + '30'
    : colors.border + '20'
  };
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(58, 134, 255, 0.2);
  }
`;

export const ChainLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  padding: 4px;

  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    padding: 3px;
  }

  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
    padding: 2px;
  }
`;

export const NetworkInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const NetworkName = styled.span`
  font-weight: 600;
  color: ${colors.text};
  font-size: 1rem;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

export const NetworkDescription = styled.span`
  font-size: 0.8rem;
  color: ${colors.textSecondary};

  @media (max-width: 768px) {
    font-size: 0.7rem;
  }

  @media (max-width: 480px) {
    font-size: 0.65rem;
  }
`;

export const DropdownArrow = styled.span<{ $open: boolean }>`
  font-size: 0.8rem;
  color: ${colors.textSecondary};
  transition: transform 0.3s ease;
  transform: ${props => props.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

export const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 9999;
  margin-top: 4px;
  background: ${colors.darker};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.4),
    0 0 60px rgba(58, 134, 255, 0.1);
  overflow: hidden;
  max-height: 300px;
  overflow-y: auto;
  pointer-events: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 0 12px 12px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  background: rgba(0, 0, 0, 0.4);
  border: none;
  border-bottom: 1px solid ${colors.border};
  padding: 0.75rem 1rem;
  color: ${colors.text};
  font-size: 0.9rem;
  outline: none;

  &::placeholder {
    color: ${colors.textSecondary};
  }

  &:focus {
    background: rgba(0, 0, 0, 0.6);
    border-bottom-color: ${colors.blue};
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.5rem;
    font-size: 0.75rem;
  }
`;

export const NoResults = styled.div`
  padding: 1rem;
  text-align: center;
  color: ${colors.textSecondary};
  font-size: 0.9rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 0.8rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    font-size: 0.75rem;
  }
`;

export const TokenRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

export const NetworkRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
`;

export const TokenColumn = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    flex: none;
    width: 100%;
  }
`;

export const SparkWalletRow = styled.div`
  display: grid;
  grid-template-columns: 200px 150px 1fr;
  gap: 1rem;
  align-items: end;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid ${colors.border};
  border-radius: 12px;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    border-color: ${colors.blue};
    background: rgba(58, 134, 255, 0.1);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    align-items: flex-start;
    padding: 1rem;
    border-radius: 9px;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    padding: 0.75rem;
    border-radius: 6px;
  }
`;

export const RemoveWalletButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: ${colors.error}30;
  border: 1px solid ${colors.error};
  color: ${colors.error};
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  line-height: 1;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.error}50;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none;
  }
`;

export const AddWalletButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${colors.blue}30;
  border: 2px dashed ${colors.blue};
  color: ${colors.primary};
  border-radius: 12px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: ${colors.blue}50;
    border-color: ${colors.primary};
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.85rem;
  }
`;

export const SparkColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
  align-items: stretch;
`;

export const SparkLabel = styled.label`
  font-size: 0.8rem;
  font-weight: 500;
  color: ${colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  height: 1.2rem;
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;

  @media (max-width: 768px) {
    font-size: 0.7rem;
    height: 1rem;
    margin-bottom: 0.2rem;
  }

  @media (max-width: 480px) {
    font-size: 0.65rem;
    height: 0.9rem;
    margin-bottom: 0.15rem;
  }
`;

export const ErrorMessage = styled.div`
  color: ${colors.error};
  font-size: 0.8rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    font-size: 0.7rem;
    margin-top: 0.375rem;
    gap: 0.375rem;
  }

  @media (max-width: 480px) {
    font-size: 0.65rem;
    margin-top: 0.25rem;
    gap: 0.25rem;
  }
`;

export const InputWithError = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Input = styled.input<{ $hasError?: boolean }>`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${props => props.$hasError ? colors.error : colors.border};
  border-radius: 12px;
  padding: 1rem;
  color: ${colors.text};
  font-size: 1rem;
  transition: all 0.3s ease;
  transform: translateY(0);

  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 0.9rem;
    border-radius: 9px;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    font-size: 0.8rem;
    border-radius: 6px;
  }

  &::placeholder {
    color: ${colors.textSecondary};
  }

  &:hover {
    border-color: ${props => props.$hasError ? colors.error : colors.blue};
    background: rgba(0, 0, 0, 0.4);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? colors.error : colors.blue};
    background: rgba(0, 0, 0, 0.5);
    transform: translateY(-1px);
    box-shadow: 0 0 0 3px ${props => props.$hasError
    ? 'rgba(255, 71, 87, 0.3)'
    : 'rgba(58, 134, 255, 0.3)'
  };
  }
`;

export const TokenIcon = styled.div<{ $color: string; $logo?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.$logo ? 'transparent' : props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.8rem;
  color: white;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    font-size: 0.7rem;
  }

  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
    font-size: 0.6rem;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`;

export const ActionButton = styled.button<{ $loading?: boolean }>`
  background: linear-gradient(45deg, ${colors.primary}, ${colors.secondary});
  border: none;
  border-radius: 16px;
  padding: 1.25rem 2rem;
  color: ${colors.text};
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 24px rgba(255, 190, 11, 0.3);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: 1rem;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    border-radius: 9px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 16px 48px rgba(255, 190, 11, 0.5);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px) scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;

    &::before {
      display: none;
    }
  }

  ${props => props.$loading && `
    &::after {
      content: '';
      position: absolute;
      width: 20px;
      height: 20px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top: 2px solid ${colors.text};
      border-radius: 50%;
      animation: spin 1s linear infinite;
      right: 1rem;
      top: 50%;
      transform: translateY(-50%);
    }

    @keyframes spin {
      0% { transform: translateY(-50%) rotate(0deg); }
      100% { transform: translateY(-50%) rotate(360deg); }
    }
  `}
`;

export const Disclaimer = styled.p`
  font-size: 0.85rem;
  color: ${colors.textMuted};
  text-align: center;
  margin-top: 2rem;
  line-height: 1.5;
  opacity: 0.8;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-top: 1.5rem;
    line-height: 1.4;
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
    margin-top: 1rem;
    line-height: 1.3;
  }
`;

// Dialog Components
export const DialogOverlay = styled.div<{ $disableAnimation?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(5, 10, 15, 0.9);
  backdrop-filter: blur(15px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: ${props => props.$disableAnimation ? 'none' : 'fadeIn 0.3s ease-out'};

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const DialogContainer = styled.div<{ $disableAnimation?: boolean }>`
  background: rgba(20, 25, 45, 0.95);
  backdrop-filter: blur(30px);
  border: 1px solid rgba(0, 229, 255, 0.2);
  border-radius: 24px;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  position: relative;
  animation: ${props => props.$disableAnimation ? 'none' : 'slideIn 0.4s ease-out'};
  box-shadow:
    0 25px 50px rgba(0, 0, 0, 0.5),
    0 0 80px rgba(0, 229, 255, 0.15);

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-30px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
    max-width: 90%;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 12px;
    max-width: 95%;
  }
`;

export const DialogHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${colors.textSecondary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: ${colors.text};
  }
`;

export const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: center;

  @media (max-width: 768px) {
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

export const DialogSubtitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${colors.text};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

export const TransferDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

export const DetailCard = styled.div`
  background: rgba(5, 10, 20, 0.8);
  border: 1px solid rgba(0, 229, 255, 0.1);
  border-radius: 12px;
  padding: 1rem;
  text-align: left;

  @media (max-width: 768px) {
    padding: 0.75rem;
    border-radius: 9px;
  }

  @media (max-width: 480px) {
    padding: 0.6rem;
    border-radius: 6px;
  }
`;

export const DetailLabel = styled.div`
  font-size: 0.75rem;
  color: ${colors.textMuted};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
`;

export const DetailValue = styled.div`
  font-size: 1.05rem;
  color: ${colors.text};
  font-weight: 600;
  word-break: break-all;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

export const CopyButton = styled.button<{ $copied?: boolean }>`
  background: ${props => props.$copied
    ? `rgba(0, 255, 136, 0.2)`
    : `rgba(0, 229, 255, 0.2)`
  };
  border: 1px solid ${props => props.$copied ? colors.success : colors.cyan};
  border-radius: 6px;
  color: ${props => props.$copied ? colors.success : colors.cyan};
  padding: 0.25rem 0.5rem;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.$copied
    ? `rgba(0, 255, 136, 0.3)`
    : `rgba(58, 134, 255, 0.3)`
  };
  }

  @media (max-width: 768px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.65rem;
  }
`;

export const QRCodeContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.75rem;
  margin: 0.5rem 0;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

export const StyledQRCode = styled(QRCodeSVG)`
  padding: 10px;
  background: white;

  @media (max-width: 768px) {
    width: 120px !important;
    height: 120px !important;
  }

  @media (max-width: 480px) {
    width: 100px !important;
    height: 100px !important;
  }
`;

export const TimeRemaining = styled.div`
  font-size: 1rem;
  color: #ffa500;
  font-weight: 600;
  text-align: center;
  padding: 0.75rem;
  background: rgba(139, 69, 19, 0.3);
  border: 1px solid rgba(139, 69, 19, 0.5);
  border-radius: 12px;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.6rem;
    border-radius: 9px;
  }
`;

export const MinDepositLabel = styled.div`
  font-size: 0.9rem;
  color: ${colors.primary};
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 0.375rem;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    margin-bottom: 0.25rem;
  }
`;